import * as React from 'react';
import { AnalyticsAction, AnalyticsEvents, EventKey, EventTransformer, PostMessageEvents } from 'src/types/Events';
import { useAnalytics } from './AnalyticsProvider';
import { usePostMessageContext } from './PostMessageProvider';

interface IProps {
  children: React.ReactNode;
}

// Forward postMessage events directly to analytics
// TODO: we'll eventually call `track()` directly from the child components,
// but this approach helps with backward compatibility.
// IMPORTANT: don't add any new tracking to this file, but prefer `AnalyticsProvider.track()` instead!
export const TemporaryAnalyticsBridge = ({ children }: IProps) => {
  const [subscribed, setSubscribed] = React.useState(false);
  const analytics = useAnalytics();

  const { eventProxy } = usePostMessageContext();

  const forwardAnalyticsEvent = React.useCallback(<T extends EventKey, A extends AnalyticsAction>(
    postMessageEventType: T,
    analyticsAction: A,
    transformer: EventTransformer<T, A>,
    options?: AddEventListenerOptions,
  ) => {
    eventProxy.addEventListener(postMessageEventType, ((e: CustomEvent) => {
      analytics.track(analyticsAction, transformer(e.detail.data as PostMessageEvents[T]) as AnalyticsEvents[A]);
    }) as EventListener, options);
  }, [eventProxy, analytics.track]);

  const subscribe = React.useCallback(() => {
    forwardAnalyticsEvent('simpleAddProtection', 'simple_add_protection', (data) => ({
      ctaVersion: '1.0.0',
      type: data.ctaType,
      productPageType: data.productPageType,
    }));

    forwardAnalyticsEvent('clydeLogoClick', 'clyde_logo', (data) => ({
      ctaVersion: '1.0.0',
      type: data.ctaType,
      ...('productPageType' in data ? { productPageType: data.productPageType } : {}),
      ...('cartType' in data ? { cartType: data.cartType } : {}),
    }));

    forwardAnalyticsEvent('learnMoreOpenModal', 'learn_more_open', (data) => ({
      ctaVersion: '1.0.0',
      type: data.ctaType,
      ...('productPageType' in data ? { productPageType: data.productPageType } : {}),
      ...('cartType' in data ? { cartType: data.cartType } : {}),
      buttonText: data.buttonText,
    }));

    const makeSelectContractEvent = (data: PostMessageEvents['selectContract']) => {
      const sku = (('sku' in data) ? data.sku : data.contractId) || undefined;
      const declineSelected = (sku === 'decline');
      const ev = {
        ctaVersion: '1.0.0',
        type: data.ctaType,
        ...('productPageType' in data ? { productPageType: data.productPageType } : {}),
        ...('cartType' in data ? { cartType: data.cartType } : {}),
        ...('modalType' in data ? { modalType: data.modalType } : {}),
        ...(declineSelected ? { declineSelected } : {}),
        selectedContractSku: declineSelected ? '' : sku,
        termSelected: data.termSelected || undefined,
      };
      return ev;
    };

    forwardAnalyticsEvent('selectContract', 'select_contract', makeSelectContractEvent);
    forwardAnalyticsEvent('selectDecline', 'select_contract', makeSelectContractEvent);
    forwardAnalyticsEvent('decline', 'decline_protection', (data) => ({
      ctaVersion: '1.0.0',
      type: data.ctaType,
      modalType: data.modalType,
      declineSource: data.declineSource,
    }));

    // Modal Events
    forwardAnalyticsEvent('showMonthlyPriceAppended', 'monthly_cost_impression', (data) => ({
      ctaVersion: '1.0.0',
      type: data.ctaType,
      modalType: data.modalType,
    }), { once: true }); // NOTE: this is only tracked once

    forwardAnalyticsEvent('viewFaq', 'faq_view', (data) => ({
      ctaVersion: '1.0.0',
      type: data.ctaType,
      modalType: data.modalType,
    }));

    forwardAnalyticsEvent('faqQueryOpened', 'faq_expanded', (data) => ({
      ctaVersion: '1.0.0',
      type: data.ctaType,
      modalType: data.modalType,
      faqOption: data.question,
      index: data.index,
    }));

    forwardAnalyticsEvent('affirmLearnMore', 'affirm_learn_more', (data) => ({
      ctaVersion: '1.0.0',
      type: 'modal',
      modalType: data.modalType,
    }));

    forwardAnalyticsEvent('affirmAppended', 'affirm_cart_impression', (data) => ({
      ctaVersion: '1.0.0',
      type: 'modal',
      modalType: data.modalType,
      cartType: data.cartType,
      paymentOption: 'affirm',
    }));

    forwardAnalyticsEvent('seeTermsAndConditions', 'see_terms_and_conditions', (data) => ({
      ctaVersion: '1.0.0',
      type: 'modal',
      modalType: data.modalType,
    }));

    // Cart Events
    forwardAnalyticsEvent('dropdownMenuOpened', 'click_dropdown', (data) => ({
      ctaVersion: '1.0.0',
      type: data.ctaType,
      cartType: data.cartType,
    }));

    forwardAnalyticsEvent('showMoreOptions', 'show_more_options', (data) => ({
      ctaVersion: '1.0.0',
      type: data.ctaType,
      productPageType: data.productPageType,
    }));

    forwardAnalyticsEvent('showFewerOptions', 'show_fewer_options', (data) => ({
      ctaVersion: '1.0.0',
      type: data.ctaType,
      productPageType: data.productPageType,
    }));

  }, [forwardAnalyticsEvent]);

  // TODO:
  // const unsubscribe = React.useCallback(() => {
  // }, [eventProxy]);

  React.useEffect(() => {
    if (analytics.ready && !subscribed) {
      subscribe();
      setSubscribed(true);
    }
    // return unsubscribe;
  }, [analytics, subscribed, setSubscribed]);

  return <>{children}</>;
};
